/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faSearch } from '@fortawesome/free-solid-svg-icons';
import { countryCodes, getDefaultCountry, searchCountries, formatPhoneNumber, parsePhoneNumber } from '../data/countryCodes';
import '../styles/PhoneInput.css';

const PhoneInput = ({ value, onChange, placeholder, required, name, id }) => {
  const defaultCountry = getDefaultCountry();
  const [selectedCountry, setSelectedCountry] = useState(defaultCountry);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCountries, setFilteredCountries] = useState(countryCodes);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 320 });
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const buttonRef = useRef(null);

  const calculateDropdownPosition = (buttonEl) => {
    const rect = buttonEl.getBoundingClientRect();
    const container = buttonEl.closest('.phone-input-container');
    const containerRect = container ? container.getBoundingClientRect() : rect;
    const viewportPadding = 12;
    const viewportWidth = window.innerWidth;
    const isMobile = viewportWidth <= 640;
    const maxDropdownWidth = isMobile
      ? Math.min(containerRect.width, viewportWidth - viewportPadding * 2)
      : Math.min(320, viewportWidth - viewportPadding * 2);
    const dropdownWidth = Math.max(200, maxDropdownWidth);
    const anchorLeft = isMobile ? containerRect.left : rect.left;
    const left = Math.max(
      viewportPadding,
      Math.min(anchorLeft, viewportWidth - dropdownWidth - viewportPadding)
    );

    return {
      top: containerRect.bottom + 8,
      left,
      width: dropdownWidth
    };
  };

  // Parse initial value if provided
  useEffect(() => {
    if (value && value.startsWith('+')) {
      // Find matching country code
      for (const country of countryCodes) {
        if (value.startsWith(country.dialCode)) {
          setSelectedCountry(country);
          setPhoneNumber(value.substring(country.dialCode.length));
          break;
        }
      }
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdownElement = document.querySelector('.country-dropdown');
      const isClickInsideButton = dropdownRef.current && dropdownRef.current.contains(event.target);
      const isClickInsideDropdown = dropdownElement && dropdownElement.contains(event.target);
      
      if (!isClickInsideButton && !isClickInsideDropdown) {
        setIsDropdownOpen(false);
        setSearchQuery('');
        setFilteredCountries(countryCodes);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isDropdownOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isDropdownOpen]);

  // Update dropdown position on scroll and resize
  useEffect(() => {
    const updateDropdownPosition = () => {
      if (isDropdownOpen && buttonRef.current) {
        setDropdownPosition(calculateDropdownPosition(buttonRef.current));
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape' && isDropdownOpen) {
        setIsDropdownOpen(false);
        setSearchQuery('');
        setFilteredCountries(countryCodes);
      }
    };

    if (isDropdownOpen) {
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
      window.addEventListener('keydown', handleEscape);
      return () => {
        window.removeEventListener('scroll', updateDropdownPosition, true);
        window.removeEventListener('resize', updateDropdownPosition);
        window.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isDropdownOpen]);

  // Add/remove class to body when dropdown is open to disable role cards
  useEffect(() => {
    if (isDropdownOpen) {
      document.body.classList.add('phone-dropdown-open');
    } else {
      document.body.classList.remove('phone-dropdown-open');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('phone-dropdown-open');
    };
  }, [isDropdownOpen]);

  // Handle search query changes
  useEffect(() => {
    if (searchQuery) {
      setFilteredCountries(searchCountries(searchQuery));
    } else {
      setFilteredCountries(countryCodes);
    }
  }, [searchQuery]);

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setIsDropdownOpen(false);
    setSearchQuery('');
    setFilteredCountries(countryCodes);
    
    // Update parent with new formatted number
    const formattedNumber = formatPhoneNumber(country.dialCode, phoneNumber);
    // Call onChange with the formatted number value
    if (typeof onChange === 'function') {
      onChange(formattedNumber);
    }
  };

  const handlePhoneNumberChange = (e) => {
    let newNumber = e.target.value;
    let countryToUse = selectedCountry;
    
    // If user enters a number starting with +, parse it to extract country code
    if (newNumber.startsWith('+')) {
      const parsed = parsePhoneNumber(newNumber);
      if (parsed.country) {
        // Update selected country if a matching country code is found
        countryToUse = parsed.country;
        setSelectedCountry(parsed.country);
        // Set phone number without country code
        newNumber = parsed.phoneNumber;
      } else {
        // If no matching country code, remove the + and use default country
        newNumber = newNumber.substring(1);
      }
    }
    
    setPhoneNumber(newNumber);
    
    // Update parent with formatted number using the correct country code
    const formattedNumber = formatPhoneNumber(countryToUse.dialCode, newNumber);
    // Call onChange with the formatted number value
    if (typeof onChange === 'function') {
      onChange(formattedNumber);
    }
  };

  const toggleDropdown = () => {
    if (!isDropdownOpen && buttonRef.current) {
      setDropdownPosition(calculateDropdownPosition(buttonRef.current));
    }
    setIsDropdownOpen(!isDropdownOpen);
  };

  return (
    <>
      <div className="phone-input-container">
        <div className="phone-input-wrapper">
          {/* Country Code Selector */}
          <div className="country-selector" ref={dropdownRef}>
            <button
              ref={buttonRef}
              type="button"
              className="country-button"
              onClick={toggleDropdown}
              aria-label="Select country code"
            >
              <span className="country-flag">{selectedCountry.flag}</span>
              <span className="country-code">{selectedCountry.dialCode}</span>
              <FontAwesomeIcon icon={faChevronDown} className={`dropdown-icon ${isDropdownOpen ? 'open' : ''}`} />
            </button>
          </div>

          {/* Phone Number Input */}
          <input
            type="tel"
            id={id}
            name={name}
            value={phoneNumber}
            onChange={handlePhoneNumberChange}
            placeholder={placeholder || "123 456 7890"}
            required={required}
            className="phone-number-input"
            pattern="[0-9\s\-\(\)\+]+"
          />
        </div>
        {!isDropdownOpen && (
          <div className="phone-input-hint">
            Format: {selectedCountry.dialCode} followed by your phone number
          </div>
        )}
      </div>

      {/* Dropdown Menu - Rendered via Portal */}
      {isDropdownOpen && createPortal(
        <div 
          className="country-dropdown"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`
          }}
        >
          <div className="dropdown-search">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search countries..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="country-list">
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country) => (
                <button
                  key={country.code}
                  type="button"
                  className={`country-item ${selectedCountry.code === country.code ? 'selected' : ''}`}
                  onClick={() => handleCountrySelect(country)}
                >
                  <span className="country-flag">{country.flag}</span>
                  <span className="country-name">{country.name}</span>
                  <span className="country-dial-code">{country.dialCode}</span>
                </button>
              ))
            ) : (
              <div className="no-results">No countries found</div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default PhoneInput;

